import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ExternalLink, Github, Code2, Cloud, Layers } from 'lucide-react';
import { toast } from 'sonner';

export const Projects = () => {
  const [filter, setFilter] = useState('all');

  const projects = [
    {
      title: 'Business Accelerator 365',
      category: 'cloud',
      description: 'Enterprise risk management and compliance application used by airports across Australia and New Zealand. Features include hazard management, external user registration, and comprehensive reporting.',
      tech: ['.NET Core', 'SQL Server', 'Power BI', 'Azure AD', 'Azure Functions', 'Redis'],
      highlights: [
        'Used by multiple airports in AU/NZ',
        'Azure Active Directory integration',
        'Real-time Power BI dashboards'
      ],
      icon: Cloud
    },
    {
      title: 'EnhancedMedical API',
      category: 'backend',
      description: 'Comprehensive insurance business API handling all operations from quotation to policy submission and claims. Supports multiple user types including customers, brokers, and underwriters.',
      tech: ['.NET Core', 'SQL Server', 'Kofax', 'Azure', 'Entity Framework Core'],
      highlights: [
        'Multi-region Azure deployment',
        'Kofax document processing',
        'Complete insurance lifecycle management'
      ],
      icon: Code2
    },
    {
      title: 'LexisNexis Source Management',
      category: 'frontend',
      description: 'Data management system for LexisNexis built with Blazor. Manages data from various sources stored in JSON format with comprehensive UI controls.',
      tech: ['Blazor', '.NET Core', 'MySQL', 'Bootstrap', 'Azure CI/CD'],
      highlights: [
        'Built from scratch to production',
        'Blazor WebAssembly architecture',
        'JSON-based data management'
      ],
      icon: Layers
    },
    {
      title: 'Optis LeaveExpert',
      category: 'cloud',
      description: 'Cloud-based leave management platform helping organizations manage leave events and navigate regulatory compliance with integrated reporting.',
      tech: ['.NET Core MVC', 'Angular 9', 'SSIS', 'SSRS', 'Kendo JS', 'SQL Server'],
      highlights: [
        'MSBI reporting integration',
        'Angular 9 frontend',
        'Regulatory compliance features'
      ],
      icon: Cloud
    },
    {
      title: 'MAF Controls Self-Assessment',
      category: 'backend',
      description: 'Enterprise controls assessment system for Majid Al Futtaim with Angular frontend and .NET Core backend. Implements comprehensive security controls.',
      tech: ['.NET Core', 'Angular', 'Azure DevOps', 'Docker', 'YAML'],
      highlights: [
        'Docker containerization',
        'Azure CI/CD with YAML',
        'Enterprise security controls'
      ],
      icon: Code2
    },
    {
      title: 'zClient Portal',
      category: 'frontend',
      description: 'Internal portal for managing clients, projects, timesheets, invoices, and documentation. Built with Blazor for rich interactive experience.',
      tech: ['Blazor', '.NET Core', 'Web API', 'SQL Server', 'Bootstrap', 'jQuery'],
      highlights: [
        'Complete project lifecycle management',
        'Timesheet and invoice automation',
        'Document management system'
      ],
      icon: Layers
    }
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter);

  const handleViewDemo = (title) => {
    toast.info(`Demo for ${title} - Contact for access`);
  };

  const handleViewCode = (title) => {
    toast.info(`Code repository - Available upon request`);
  };

  return (
    <section id="projects" className="py-16 sm:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Featured <span className="text-gradient-azure">Projects</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-azure mx-auto rounded-full"></div>
        </div>

        {/* Filter Tabs */}
        <Tabs defaultValue="all" className="max-w-7xl mx-auto" onValueChange={setFilter}>
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-4 mb-8 sm:mb-12">
            <TabsTrigger value="all" className="text-xs sm:text-sm">All</TabsTrigger>
            <TabsTrigger value="backend" className="text-xs sm:text-sm">Backend</TabsTrigger>
            <TabsTrigger value="frontend" className="text-xs sm:text-sm">Frontend</TabsTrigger>
            <TabsTrigger value="cloud" className="text-xs sm:text-sm">Cloud</TabsTrigger>
          </TabsList>

          <TabsContent value={filter} className="mt-0">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project, index) => {
                const Icon = project.icon;
                return (
                  <Card
                    key={index}
                    className="glass-card border-border/50 hover:shadow-elegant transition-all duration-300 flex flex-col animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <Badge variant="secondary" className="text-xs capitalize">
                          {project.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg sm:text-xl">{project.title}</CardTitle>
                      <CardDescription className="text-sm">
                        {project.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="flex-1">
                      {/* Highlights */}
                      <ul className="space-y-2 mb-4 text-sm text-muted-foreground">
                        {project.highlights.map((highlight, hIndex) => (
                          <li key={hIndex} className="flex items-start">
                            <span className="text-primary mr-2 font-bold">✓</span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Tech Stack */}
                      <div className="flex flex-wrap gap-2">
                        {project.tech.map((tech, tIndex) => (
                          <Badge key={tIndex} variant="outline" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>

                    <CardFooter className="flex gap-2 pt-4 border-t border-border/50 mt-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleViewDemo(project.title)}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Demo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleViewCode(project.title)}
                      >
                        <Github className="w-4 h-4 mr-1" />
                        Code
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};